import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { MessageSquarePlus, Search, Plus, FolderGit2 } from 'lucide-react';
import { ChatInterface } from '@/components/chat/ChatInterface';
import { ConversationItem } from '@/components/conversations/ConversationItem';
import { ResizableSidebarLayout } from '@/components/layout/ResizableSidebarLayout';
import { GithubRepoPickerDialog } from '@/components/sidebar/GithubRepoPickerDialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useProject } from '@/contexts/ProjectContext';
import { useWorkArea } from '@/contexts/WorkAreaContext';
import { listConversations, listWorkflowRuns, listWorkAreaCodebases } from '@/lib/api';
import type { CodebaseResponse, WorkArea } from '@/lib/api';
import { cn } from '@/lib/utils';

const PANEL_MIN = 220;
const PANEL_MAX = 420;
const PANEL_DEFAULT = 260;
const STORAGE_KEY = 'metis-chat-panel-width';

export function ChatPage(): React.ReactElement {
  const { '*': rawConversationId } = useParams();
  const conversationId = rawConversationId ? decodeURIComponent(rawConversationId) : undefined;

  const navigate = useNavigate();
  const {
    selectedProjectId: selectedCodebaseId,
    setSelectedProjectId: setSelectedCodebaseId,
    codebases,
    isLoadingCodebases,
  } = useProject();
  const { selectedWorkAreaId, setSelectedWorkAreaId, workAreas, isLoadingWorkAreas } =
    useWorkArea();

  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [githubRepoPickerOpen, setGithubRepoPickerOpen] = useState(false);

  const {
    data: workAreaCodebaseMemberships,
    isLoading: isLoadingWorkAreaCodebases,
    isError: isWorkAreaCodebasesError,
  } = useQuery({
    queryKey: ['workAreaCodebases', selectedWorkAreaId],
    queryFn: () => listWorkAreaCodebases(selectedWorkAreaId ?? ''),
    enabled: selectedWorkAreaId !== null,
  });

  const { data: runs } = useQuery({
    queryKey: ['workflow-runs-status'],
    queryFn: () => listWorkflowRuns({ limit: 50 }),
    refetchInterval: 10_000,
  });

  const conversationStatusMap = useMemo((): Map<string, 'running' | 'failed'> => {
    const map = new Map<string, 'running' | 'failed'>();
    if (!runs) return map;
    for (const run of runs) {
      // For web runs, parent_conversation_id is the visible conversation in the sidebar.
      // For CLI runs, conversation_id is the only conversation (no parent/worker split).
      const key = run.parent_conversation_id ?? run.conversation_id;
      if (!key) continue;
      if (run.status === 'running') {
        map.set(key, 'running');
      } else if (run.status === 'failed' && !map.has(key)) {
        map.set(key, 'failed');
      }
    }
    return map;
  }, [runs]);

  const codebaseMap = useMemo((): Map<string, CodebaseResponse> => {
    const map = new Map<string, CodebaseResponse>();
    if (codebases) {
      for (const cb of codebases) {
        map.set(cb.id, cb);
      }
    }
    return map;
  }, [codebases]);

  const workAreaMap = useMemo((): Map<string, WorkArea> => {
    const map = new Map<string, WorkArea>();
    if (workAreas) {
      for (const workArea of workAreas) {
        map.set(workArea.id, workArea);
      }
    }
    return map;
  }, [workAreas]);

  const memberCodebaseIds = useMemo((): Set<string> | null => {
    if (!workAreaCodebaseMemberships) return null;
    return new Set(workAreaCodebaseMemberships.codebases.map(membership => membership.codebaseId));
  }, [workAreaCodebaseMemberships]);

  const availableCodebases = useMemo((): CodebaseResponse[] | undefined => {
    if (!codebases) return undefined;
    if (selectedWorkAreaId && !memberCodebaseIds) return undefined;
    if (selectedWorkAreaId && memberCodebaseIds) {
      return codebases.filter(codebase => memberCodebaseIds.has(codebase.id));
    }
    return codebases;
  }, [codebases, memberCodebaseIds, selectedWorkAreaId]);

  const isLoadingAvailableCodebases =
    isLoadingCodebases || (selectedWorkAreaId !== null && isLoadingWorkAreaCodebases);
  const selectedCodebaseIsValidForWorkArea =
    !selectedWorkAreaId ||
    !selectedCodebaseId ||
    (memberCodebaseIds?.has(selectedCodebaseId) ?? false);
  const scopedCodebaseId = selectedCodebaseIsValidForWorkArea ? selectedCodebaseId : null;

  const { data: conversations } = useQuery({
    queryKey: ['conversations', scopedCodebaseId, selectedWorkAreaId],
    queryFn: () =>
      listConversations(scopedCodebaseId ?? undefined, selectedWorkAreaId ?? undefined),
    refetchInterval: 10_000,
  });

  useEffect(() => {
    if (!selectedWorkAreaId || !selectedCodebaseId || !memberCodebaseIds) return;
    if (!memberCodebaseIds.has(selectedCodebaseId)) {
      setSelectedCodebaseId(null);
    }
  }, [memberCodebaseIds, selectedCodebaseId, selectedWorkAreaId, setSelectedCodebaseId]);

  const filtered = useMemo(
    () =>
      conversations?.filter(conv => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (conv.title ?? conv.platform_conversation_id).toLowerCase().includes(query);
      }),
    [conversations, searchQuery]
  );

  const handleNewChat = useCallback((): void => {
    navigate('/chat');
  }, [navigate]);

  return (
    <ResizableSidebarLayout
      storageKey={STORAGE_KEY}
      minWidth={PANEL_MIN}
      defaultWidth={PANEL_DEFAULT}
      maxWidth={PANEL_MAX}
      sidebar={
        <>
          {/* New Chat button */}
          <div className="px-3 pt-3 pb-2">
            <button
              onClick={handleNewChat}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-[oklch(0.82_0.13_220)] bg-[linear-gradient(180deg,oklch(0.78_0.14_225)_0%,oklch(0.68_0.15_230)_100%)] px-3 py-2.5 text-sm font-semibold text-primary-foreground shadow-[inset_0_1px_0_oklch(1_0_0/0.3),0_1px_2px_oklch(0_0_0/0.4)] transition-colors hover:brightness-110"
            >
              <MessageSquarePlus className="size-4 shrink-0" strokeWidth={2.5} />
              New Chat
            </button>
          </div>

          {/* Work Area filter */}
          <div className="px-3 pb-2">
            <div className="mb-1 flex items-center justify-between">
              <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-text-tertiary">
                Work Area
              </span>
            </div>

            {isLoadingWorkAreas ? (
              <div className="flex items-center justify-center py-2">
                <span className="text-xs text-text-tertiary">Loading…</span>
              </div>
            ) : (
              <select
                value={selectedWorkAreaId ?? ''}
                onChange={(e): void => {
                  setSelectedWorkAreaId(e.target.value || null);
                }}
                className="w-full rounded-md border border-border bg-surface-elevated px-2 py-1.5 text-xs text-text-primary focus:border-primary focus:outline-none"
              >
                <option value="">All Work Areas</option>
                {workAreas?.map(workArea => (
                  <option key={workArea.id} value={workArea.id}>
                    {workArea.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Codebase filter */}
          <div className="px-3 pb-2">
            <div className="flex items-center justify-between mb-1">
              <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-text-tertiary">
                Codebase
              </span>
              <button
                onClick={(): void => {
                  setGithubRepoPickerOpen(true);
                }}
                className="p-1 rounded hover:bg-surface-elevated transition-colors"
                title="Add codebase"
              >
                <Plus className="size-3.5 text-text-tertiary hover:text-primary" />
              </button>
            </div>

            {isWorkAreaCodebasesError ? (
              <div className="rounded-md border border-error/30 bg-error/5 px-2 py-1.5 text-xs text-error">
                Unable to load Work Area codebases
              </div>
            ) : isLoadingAvailableCodebases ? (
              <div className="flex items-center justify-center py-2">
                <span className="text-xs text-text-tertiary">Loading…</span>
              </div>
            ) : (
              <select
                value={scopedCodebaseId ?? ''}
                onChange={(e): void => {
                  setSelectedCodebaseId(e.target.value || null);
                }}
                className="w-full rounded-md border border-border bg-surface-elevated px-2 py-1.5 text-xs text-text-primary focus:border-primary focus:outline-none"
              >
                <option value="">All Codebases</option>
                {availableCodebases?.map(cb => (
                  <option key={cb.id} value={cb.id}>
                    {cb.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <Separator className="bg-border" />

          {/* Search */}
          <div className="px-3 py-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 size-3 -translate-y-1/2 text-text-tertiary" />
              <input
                ref={searchInputRef}
                value={searchQuery}
                onChange={(e): void => {
                  setSearchQuery(e.target.value);
                }}
                placeholder="Search…"
                className="w-full rounded-md border border-border bg-surface-elevated py-1.5 pl-7 pr-14 text-xs text-text-primary placeholder:text-text-tertiary focus:border-primary focus:outline-none"
              />
              <span className="pointer-events-none absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-0.5">
                <kbd className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded border border-border bg-surface-elevated px-1 font-mono text-[10px] text-text-tertiary shadow-[inset_0_-1px_0_var(--surface-inset)]">
                  ⌘
                </kbd>
                <kbd className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded border border-border bg-surface-elevated px-1 font-mono text-[10px] text-text-tertiary shadow-[inset_0_-1px_0_var(--surface-inset)]">
                  K
                </kbd>
              </span>
            </div>
          </div>

          {/* Conversation list */}
          <ScrollArea className="flex-1 min-h-0 px-2 pb-2">
            <div className="flex flex-col gap-0.5">
              {filtered && filtered.length > 0 ? (
                filtered.map(conv => (
                  <ConversationItem
                    key={conv.id}
                    conversation={conv}
                    codebaseName={
                      conv.codebase_id ? codebaseMap.get(conv.codebase_id)?.name : undefined
                    }
                    workAreaName={
                      conv.work_area_id
                        ? (workAreaMap.get(conv.work_area_id)?.name ?? conv.work_area_id)
                        : undefined
                    }
                    status={conversationStatusMap.get(conv.id) ?? 'idle'}
                  />
                ))
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 py-8 px-4">
                  <FolderGit2 className="size-8 text-text-tertiary" />
                  <span
                    className={cn(
                      'text-xs text-text-tertiary text-center',
                      conversations && conversations.length > 0 ? '' : ''
                    )}
                  >
                    {conversations && conversations.length > 0
                      ? 'No matching conversations'
                      : 'No conversations yet — start a new chat!'}
                  </span>
                </div>
              )}
            </div>
          </ScrollArea>
        </>
      }
    >
      <ChatInterface
        key={conversationId ?? 'new'}
        conversationId={conversationId ?? 'new'}
        selectedCodebaseId={scopedCodebaseId}
        selectedWorkAreaId={selectedWorkAreaId}
      />
      <GithubRepoPickerDialog
        open={githubRepoPickerOpen}
        onOpenChange={setGithubRepoPickerOpen}
        onAdded={codebase => {
          setSelectedCodebaseId(codebase.id);
        }}
      />
    </ResizableSidebarLayout>
  );
}
